// BI.1.2 Add Linked Account
import { each, select } from '../shared/utils/dom';
import { delimiters } from '../shared/constants';

import { linkedAccountsApi } from './api';

// main mixin
import loadingMixin from '../shared/vue/mixins/loading';
import scrollUp from '../shared/vue/mixins/scrollUp';
import formMixin from '../shared/vue/mixins/form';
import { isAreaCodePhoneValid } from '../shared/constants/validation';
import { createError, removeError } from '../shared/formErrors';

export default function vueInit() {
  const el = select('[data-vue-app="edit-contact-info"]');
  if (el) {
    const app = new Vue({ // eslint-disable-line no-unused-vars
      el,
      delimiters,
      mixins: [loadingMixin, formMixin, scrollUp],
      data: {
        customerAccountId: '',
        agree: false,
        date: '',
        linkedAccountCount: 0,
        disabledAccountNumber: false,
        modelAddressType: 'Regular',
        modelCity: '',
        modelStandardAddress: {
          apartmentNumber: '',
          buildingNumber: '',
          prefix: '',
          streetName: '',
          streetNumber: '',
          suffix1: '',
          suffix2: '',
          suite: '',
        },
        modelDateOfBirth: null,
        modelLast4Ssn: null,
        modelDisAddress: {
          addressLine1: '',
          addressLine2: '',
        },
        modelPOBox: '',
        modelRuralAddress: {
          rrNumber: '',
          rrBoxNumber: '',
        },
        modelServiceAddresses: [],
        modelState: '',
        modelZip: '',
        modelZipPlusFour: '',
        modelAdditionalText: '',
        modelHomePhone: '',
        modelBusinessPhone: '',
        modelMobilePhone: '',
        modelLot: '',
        overridden: false,
        success: false,
        errorMessage: null,
        liveText: '',
        phoneType: '',
        phoneNumber: '',
        phoneNumbers: [],
        isReady: false,
        modelLdc: '',
        showUnlimitedPhoneFields: false,
        showNormalPhoneFields: false,
        isCisAccount: false,
        newPhoneNumberIsValid: false,
        hasError: false,
      },

      mounted() {
        // Find the LDC for the account being edited
        // This will be added to the DOM via back-end.
        if (this.$el.dataset.ldc) {
          this.modelLdc = this.$el.dataset.ldc;
          // now we're ready to mask
          this.isReady = true;
          if (this.modelLdc === 'NI' || this.modelLdc === 'BS') {
            this.showUnlimitedPhoneFields = true;
            this.isCisAccount = true;
          } else {
            this.showNormalPhoneFields = true;
            this.isCisAccount = false;
          }
        }

        Vue.nextTick(() => {
          this.loading = true;
          this.customerAccountId = this.$el.dataset.customerAccountNumber;
          if (window.accountData) {
            // We're in Fabricator, so use the mocked account data
            this.loading = false;
            if (window.accountData.mailingAddress) {
              this.populateMailingAddress(window.accountData.mailingAddress);
            }
            if (window.accountData.contactInformation) {
              this.populatePhoneNumbers(window.accountData.contactInformation);
            }
          } else {
            linkedAccountsApi.getContactInfo(this.customerAccountId)
              .then(contactInfo => {
                this.loading = false;
                if (contactInfo && contactInfo.data) {
                  if (contactInfo.data.mailingAddress != null) {
                    this.populateMailingAddress(contactInfo.data.mailingAddress);
                  }
                  if (contactInfo.data.phoneNumbers.length) {
                    this.populatePhoneNumbers(contactInfo.data);
                  }
                }
              })
              .catch(this.handleApiError)
          }
        });
      },

      watch: {
        modelAddressType(newValue, oldValue) {
          if (oldValue === 'POBox') {
            removeError(this.$refs.poBox, this.$refs.poBoxError, this.$refs.poBox.parentNode.querySelector('.asterisk'));
          } else if (oldValue === 'Rural') {
            removeError(this.$refs.rrNumber, this.$refs.rrNumberError, this.$refs.rrNumber.parentNode.querySelector('.asterisk'));
            removeError(this.$refs.rrBoxNumber, this.$refs.rrBoxNumberError, this.$refs.rrBoxNumber.parentNode.querySelector('.asterisk'));
          }
          removeError(this.$refs.cityInput, this.$refs.cityStateZipError, this.$refs.cityInput.parentNode.querySelector('.asterisk'));
          removeError(this.$refs.modelState, this.$refs.cityStateZipError, document.querySelector('label[for="state"]').querySelector('.asterisk'));
          removeError(this.$refs.zipInput, this.$refs.cityStateZipError, this.$refs.zipInput.parentNode.querySelector('.asterisk'));
          removeError(this.$refs.zipPlusFour, this.$refs.cityStateZipError);
        },
        modelCity(val) {
          if (this.modelLdc === 'BS') {
            this.modelCity = val.replace(/,/g, '');
          }
        },
      },

      methods: {
        handleApiError(err) { // eslint-disable-line no-unused-vars
          this.loading = false;
          this.success = false;
          this.scrollUp();
        },
        validatePhoneNumber() {
          const addPhoneError = select('#add-phone-error');
          const phoneNumberInput = select('#phone-number');
          if (isAreaCodePhoneValid(this.phoneNumber)) {
            this.newPhoneNumberIsValid = true;
            removeError(phoneNumberInput, addPhoneError);
          } else {
            this.newPhoneNumberIsValid = false;
            createError(phoneNumberInput, addPhoneError, 'Please provide a valid phone number.');
          }
        },
        addPhoneNumber() {
          const phoneNumber = {
            type: this.phoneType,
            number: this.phoneNumber,
          };
          phoneNumber.number = phoneNumber.number.replace(/\D/g, "");
          this.validateCISPhone();
          if (this.newPhoneNumberIsValid) {
            this.loading = true;
            this.phoneNumbers.push(phoneNumber);
            this.phoneType = null;
            this.phoneNumber = null;
            this.loading = false;
            this.newPhoneNumberIsValid = false;
          }
        },
        deletePhoneNumber(number) {
          // remove the deleted number from the array of phone numbers
          const numberToDelete = this.phoneNumbers.indexOf(number);
          this.phoneNumbers.splice(numberToDelete, 1);
        },
        validateDISAddress() {
          if (!this.modelDisAddress.addressLine1 && !this.modelDisAddress.addressLine2 && !this.modelCity && !this.modelState && !this.modelZip && !this.modelZipPlusFour) {
            removeError(this.$refs.addressLine1, this.$refs.addressLine1Error, this.$refs.addressLine1.parentNode.querySelector('.asterisk'));
            removeError(this.$refs.addressLine2, this.$refs.addressLine2Error);
            removeError(this.$refs.cityInput, this.$refs.cityStateZipError, this.$refs.cityInput.parentNode.querySelector('.asterisk'));
            removeError(this.$refs.modelState, this.$refs.cityStateZipError, document.querySelector('label[for="state"]').querySelector('.asterisk'));
            removeError(this.$refs.zipInput, this.$refs.cityStateZipError, this.$refs.zipInput.parentNode.querySelector('.asterisk'));
            removeError(this.$refs.zipPlusFour, this.$refs.cityStateZipError);
          } else {
            // validate city, state, and zip fields
            const isAddressValid = this.modelDisAddress.addressLine1 && this.modelDisAddress.addressLine1.length > 3 && !RegExp(/[{}]/g).test(this.modelDisAddress.addressLine1);
            const isAddress2Valid = !this.modelDisAddress.addressLine2 || !RegExp(/[{}]/g).test(this.modelDisAddress.addressLine2);
            const isCityValid = this.modelCity && this.modelCity.trim().length >= 2 && !RegExp(/[{}]/g).test(this.modelCity);
            const isStateValid = this.modelState && this.modelState.length === 2;
            const isZipValid = this.modelZip && this.modelZip.toString().length === 5 && /\d/g.test(this.modelZip);
            const isZipPlusFourValid = !this.modelZipPlusFour || (this.modelZipPlusFour.toString().length === 4 && /\d/g.test(this.modelZipPlusFour));

            // check if at least one input is valid AND one or more is invalid, then run individual validation on each field
            if ((isAddressValid || isAddress2Valid || isCityValid || isZipValid || isStateValid || isZipPlusFourValid) && (!isAddressValid || !isAddress2Valid || !isCityValid || !isZipValid || !isStateValid || !isZipPlusFourValid)) {
              // validate street address field
              if (isAddressValid) {
                removeError(this.$refs.addressLine1, this.$refs.addressLine1Error, this.$refs.addressLine1.parentNode.querySelector('.asterisk'));
              } else {
                createError(this.$refs.addressLine1, this.$refs.addressLine1Error, 'Please provide a valid street address.', this.$refs.addressLine1.parentNode.querySelector('.asterisk'));
                this.hasError = true;
              }

              if (isAddress2Valid) {
                removeError(this.$refs.addressLine2, this.$refs.addressLine2Error);
              } else {
                createError(this.$refs.addressLine2, this.$refs.addressLine2Error, 'Please provide a valid street address.');
                this.hasError = true;
              }

              // only remove message if all fields are valid since they share the same error message, otherwise check individual fields
              if (isCityValid && isStateValid && isZipValid && isZipPlusFourValid) {
                removeError(this.$refs.cityInput, this.$refs.cityStateZipError, this.$refs.cityInput.parentNode.querySelector('.asterisk'));
                removeError(this.$refs.modelState, this.$refs.cityStateZipError, document.querySelector('label[for="state"]').querySelector('.asterisk'));
                removeError(this.$refs.zipInput, this.$refs.cityStateZipError, this.$refs.zipInput.parentNode.querySelector('.asterisk'));
                removeError(this.$refs.zipPlusFour, this.$refs.cityStateZipError);
              } else {
                // check validation for city input field
                if (isCityValid) {
                  removeError(this.$refs.cityInput, this.$refs.cityInput.parentNode.querySelector('.asterisk'));
                } else {
                  createError(this.$refs.cityInput, this.$refs.cityStateZipError, 'Please provide a valid city, state and zip code.', this.$refs.cityInput.parentNode.querySelector('.asterisk'));
                  this.hasError = true;
                }

                // check validation for state input field
                if (isStateValid) {
                  removeError(this.$refs.modelState, document.querySelector('label[for="state"]').querySelector('.asterisk'));
                } else {
                  createError(this.$refs.modelState, this.$refs.cityStateZipError, 'Please provide a valid city, state and zip code.', document.querySelector('label[for="state"]').querySelector('.asterisk'));
                  this.hasError = true;
                }

                // check validation for zip input field
                if (isZipValid) {
                  removeError(this.$refs.zipInput, this.$refs.zipInput.parentNode.querySelector('.asterisk'));
                } else {
                  createError(this.$refs.zipInput, this.$refs.cityStateZipError, 'Please provide a valid city, state and zip code.', this.$refs.zipInput.parentNode.querySelector('.asterisk'));
                  this.hasError = true;
                }

                // check validation for zipPlusFour input field
                if (isZipPlusFourValid) {
                  removeError(this.$refs.zipPlusFour);
                } else {
                  createError(this.$refs.zipPlusFour, this.$refs.cityStateZipError, 'Please provide a valid city, state and zip code.');
                  this.hasError = true;
                }
              }
            } else {
              removeError(this.$refs.addressLine1, this.$refs.addressLine1Error, this.$refs.addressLine1.parentNode.querySelector('.asterisk'));
              removeError(this.$refs.addressLine2, this.$refs.addressLine2Error);
              removeError(this.$refs.cityInput, this.$refs.cityStateZipError, this.$refs.cityInput.parentNode.querySelector('.asterisk'));
              removeError(this.$refs.modelState, this.$refs.cityStateZipError, document.querySelector('label[for="state"]').querySelector('.asterisk'));
              removeError(this.$refs.zipInput, this.$refs.cityStateZipError, this.$refs.zipInput.parentNode.querySelector('.asterisk'));
              removeError(this.$refs.zipPlusFour, this.$refs.cityStateZipError);
            }
          }
        },
        validateDISPhone() {
          // validate home phone
          const homeNumberInput = select('#homePhone');

          if (!this.modelHomePhone || isAreaCodePhoneValid(this.modelHomePhone)) {
            removeError(homeNumberInput, this.$refs.homePhoneError);
          } else {
            createError(homeNumberInput, this.$refs.homePhoneError, 'Please provide a valid phone number.');
            this.hasError = true;
          }

          // validate business phone
          const businessNumberInput = select('#businessPhone');

          if (!this.modelBusinessPhone || isAreaCodePhoneValid(this.modelBusinessPhone)) {
            removeError(businessNumberInput, this.$refs.businessPhoneError);
          } else {
            createError(businessNumberInput, this.$refs.businessPhoneError, 'Please provide a valid phone number.');
            this.hasError = true;
          }

          // validate mobile/other phone
          const mobilePhoneError = select('#mobile-phone-error');
          const mobileNumberInput = select('#mobilePhone');

          if (!this.modelMobilePhone || isAreaCodePhoneValid(this.modelMobilePhone)) {
            removeError(mobileNumberInput, mobilePhoneError);
          } else {
            createError(mobileNumberInput, mobilePhoneError, 'Please provide a valid phone number.');
            this.hasError = true;
          }
        },
        validateCISAddress() {
          const isRegularAddress = this.modelAddressType === 'Regular';
          const isPOBoxAddress = this.modelAddressType === 'POBox';
          const isRuralAddress = this.modelAddressType === 'Rural';
          // validate city, state, zip, lot , rr, rr box, po box.
          if (!this.modelStandardAddress.streetNumber && !this.modelStandardAddress.streetName
            && !this.modelCity && !this.modelState && !this.modelZip && !this.modelZipPlusFour
            && !this.modelLot && !this.modelPOBox && !this.modelRuralAddress.rrNumber && !this.modelRuralAddress.rrBoxNumber) {
            removeError(this.$refs.cityInput, this.$refs.cityStateZipError, this.$refs.cityInput.parentNode.querySelector('.asterisk'));
            removeError(this.$refs.modelState, this.$refs.cityStateZipError, document.querySelector('label[for="state"]').querySelector('.asterisk'));
            removeError(this.$refs.zipInput, this.$refs.cityStateZipError, this.$refs.zipInput.parentNode.querySelector('.asterisk'));
            removeError(this.$refs.zipPlusFour, this.$refs.cityStateZipError);

            if (isRegularAddress) {
              removeError(this.$refs.streetNumber, this.$refs.streetNumberError, this.$refs.streetNumber.parentNode.querySelector('.asterisk'));
              removeError(this.$refs.streetName, this.$refs.streetNameError, this.$refs.streetName.parentNode.querySelector('.asterisk'));
              removeError(this.$refs.lotInput, this.$refs.lotError);
            } else if (isPOBoxAddress) {
              removeError(this.$refs.poBox, this.$refs.poBoxError);
            } else if (isRuralAddress) {
              removeError(this.$refs.rrNumber, this.$refs.rrNumberError);
              removeError(this.$refs.rrBoxNumber, this.$refs.rrBoxNumberError);
            }
          } else {
            const isStreetNumberValid = this.modelStandardAddress.streetNumber && RegExp(/^[a-z0-9]+$/i).test(this.modelStandardAddress.streetNumber);
            const isStreetNameValid = this.modelStandardAddress.streetName && this.modelStandardAddress.streetName.trim().length > 3 && !RegExp(/[{}]/g).test(this.modelStandardAddress.streetName);
            const isCityValid = this.modelCity && this.modelCity.trim().length >= 2 && !RegExp(/[{}]/g).test(this.modelCity);
            const isStateValid = this.modelState && this.modelState.length === 2;
            const isZipValid = this.modelZip && this.modelZip.toString().length === 5 && /\d/g.test(this.modelZip);
            const isZipPlusFourValid = !this.modelZipPlusFour || (this.modelZipPlusFour.toString().length === 4 && /\d/g.test(this.modelZipPlusFour));
            const isLotValid = !this.modelLot || (this.modelLot.toString().length <= 5 && RegExp(/^[a-z0-9]+$/i).test(this.modelLot));
            const isPoBoxValid = this.modelPOBox && isPOBoxAddress && isCityValid && isStateValid && isZipValid && RegExp(/^[a-z0-9]+$/i).test(this.modelPOBox);
            const isRRNumberValid = this.modelRuralAddress.rrNumber && isRuralAddress && isCityValid && isStateValid && isZipValid && RegExp(/^[a-z0-9]+$/i).test(this.modelRuralAddress.rrNumber);
            const isRRBoxNumberValid = this.modelRuralAddress.rrBoxNumber && isRuralAddress && isCityValid && isStateValid && isZipValid && RegExp(/^[a-z0-9]+$/i).test(this.modelRuralAddress.rrBoxNumber);
            const isApartmentValid = !this.modelStandardAddress.apartmentNumber || RegExp(/^[a-z0-9]+$/i).test(this.modelStandardAddress.apartmentNumber);
            const isSuiteValid = !this.modelStandardAddress.suite || RegExp(/^[a-z0-9]+$/i).test(this.modelStandardAddress.suite);
            const isBuildingValid = !this.modelStandardAddress.buildingNumber || RegExp(/^[a-z0-9]+$/i).test(this.modelStandardAddress.buildingNumber);

            if (isRegularAddress) {
              if (isApartmentValid && isSuiteValid && isBuildingValid) {
                removeError(this.$refs.apartmentNumber, this.$refs.apartmentSuiteBuildingError);
                removeError(this.$refs.suite, this.$refs.apartmentSuiteBuildingError);
                removeError(this.$refs.buildingNumber, this.$refs.apartmentSuiteBuildingError);
              } else {
                if (isApartmentValid) {
                  removeError(this.$refs.apartmentNumber);
                } else {
                  createError(this.$refs.apartmentNumber, this.$refs.apartmentSuiteBuildingError, 'Please provide a valid apartment #, suite or building #.');
                  this.hasError = true;
                }

                if (isSuiteValid) {
                  removeError(this.$refs.suite);
                } else {
                  createError(this.$refs.suite, this.$refs.apartmentSuiteBuildingError, 'Please provide a valid apartment #, suite or building #.');
                  this.hasError = true;
                }

                if (isBuildingValid) {
                  removeError(this.$refs.buildingNumber);
                } else {
                  createError(this.$refs.buildingNumber, this.$refs.apartmentSuiteBuildingError, 'Please provide a valid apartment #, suite or building #.');
                  this.hasError = true;
                }
              }
            }

            // check if at least one input is valid AND one or more is invalid, then run individual validation on each field
            if ((isStreetNumberValid || isStreetNameValid || isCityValid || isZipValid || isStateValid || isZipPlusFourValid || isLotValid || isPoBoxValid
              || isRRNumberValid || isRRBoxNumberValid)
              && (!isStreetNumberValid || !isStreetNameValid || !isCityValid || !isZipValid || !isStateValid || !isZipPlusFourValid || !isLotValid || !isPoBoxValid
                || !isRRNumberValid || !isRRBoxNumberValid)) {
              // validate street address field

              if (isRegularAddress) {
                if (isStreetNumberValid) {
                  removeError(this.$refs.streetNumber, this.$refs.streetNumberError, this.$refs.streetNumber.parentNode.querySelector('.asterisk'));
                } else {
                  createError(this.$refs.streetNumber, this.$refs.streetNumberError, 'Please provide a valid street address.', this.$refs.streetNumber.parentNode.querySelector('.asterisk'));
                  this.hasError = true;
                }

                if (isStreetNameValid) {
                  removeError(this.$refs.streetName, this.$refs.streetNameError, this.$refs.streetName.parentNode.querySelector('.asterisk'));
                } else {
                  createError(this.$refs.streetName, this.$refs.streetNameError, 'Please provide a valid street name.', this.$refs.streetName.parentNode.querySelector('.asterisk'));
                  this.hasError = true;
                }
              }

              // only remove message if all fields are valid since they share the same error message, otherwise check individual fields
              if (isCityValid && isStateValid && isZipValid && isZipPlusFourValid) {
                removeError(this.$refs.cityInput, this.$refs.cityStateZipError, this.$refs.cityInput.parentNode.querySelector('.asterisk'));
                removeError(this.$refs.modelState, this.$refs.cityStateZipError, document.querySelector('label[for="state"]').querySelector('.asterisk'));
                removeError(this.$refs.zipInput, this.$refs.cityStateZipError, this.$refs.zipInput.parentNode.querySelector('.asterisk'));
                removeError(this.$refs.zipPlusFour, this.$refs.cityStateZipError);
              } else {
                // check validation for city input field
                if (isCityValid) {
                  removeError(this.$refs.cityInput, this.$refs.cityInput.parentNode.querySelector('.asterisk'));
                } else {
                  createError(this.$refs.cityInput, this.$refs.cityStateZipError, 'Please provide a valid city, state and zip code.', this.$refs.cityInput.parentNode.querySelector('.asterisk'));
                  this.hasError = true;
                }

                // check validation for state input field
                if (isStateValid) {
                  removeError(this.$refs.modelState, document.querySelector('label[for="state"]').querySelector('.asterisk'));
                } else {
                  createError(this.$refs.modelState, this.$refs.cityStateZipError, 'Please provide a valid city, state and zip code.', document.querySelector('label[for="state"]').querySelector('.asterisk'));
                  this.hasError = true;
                }

                // check validation for zip input field
                if (isZipValid) {
                  removeError(this.$refs.zipInput, this.$refs.zipInput.parentNode.querySelector('.asterisk'));
                } else {
                  createError(this.$refs.zipInput, this.$refs.cityStateZipError, 'Please provide a valid city, state and zip code.', this.$refs.zipInput.parentNode.querySelector('.asterisk'));
                  this.hasError = true;
                }

                // check validation for zipPlusFour input field
                if (isZipPlusFourValid) {
                  removeError(this.$refs.zipPlusFour);
                } else {
                  createError(this.$refs.zipPlusFour, this.$refs.cityStateZipError, 'Please provide a valid city, state and zip code.');
                  this.hasError = true;
                }
              }

              if (isPOBoxAddress) {
                if (isPoBoxValid) {
                  removeError(this.$refs.poBox, this.$refs.poBoxError, this.$refs.poBox.parentNode.querySelector('.asterisk'));
                } else {
                  createError(this.$refs.poBox, this.$refs.poBoxError, 'Please provide a valid PO Box.', this.$refs.poBox.parentNode.querySelector('.asterisk'));
                  this.hasError = true;
                }
              }

              if (isRuralAddress) {
                if (isRRNumberValid) {
                  removeError(this.$refs.rrNumber, this.$refs.rrNumberError, this.$refs.rrNumber.parentNode.querySelector('.asterisk'));
                } else {
                  createError(this.$refs.rrNumber, this.$refs.rrNumberError, 'Please provide a valid RR #.', this.$refs.rrNumber.parentNode.querySelector('.asterisk'));
                  this.hasError = true;
                }

                if (isRRBoxNumberValid) {
                  removeError(this.$refs.rrBoxNumber, this.$refs.rrBoxNumberError, this.$refs.rrBoxNumber.parentNode.querySelector('.asterisk'));
                } else {
                  createError(this.$refs.rrBoxNumber, this.$refs.rrBoxNumberError, 'Please provide a valid RR Box #.', this.$refs.rrBoxNumber.parentNode.querySelector('.asterisk'));
                  this.hasError = true;
                }
              }

              if (isRegularAddress) {
                if (isLotValid) {
                  removeError(this.$refs.lotInput, this.$refs.lotError);
                } else {
                  createError(this.$refs.lotInput, this.$refs.lotError, 'Please provide a valid Lot.');
                  this.hasError = true;
                }
              }
            } else {
              if (isRegularAddress) {
                removeError(this.$refs.streetNumber, this.$refs.streetNumberError, this.$refs.streetNumber.parentNode.querySelector('.asterisk'));
                removeError(this.$refs.streetName, this.$refs.streetNameError, this.$refs.streetName.parentNode.querySelector('.asterisk'));
              }

              removeError(this.$refs.cityInput, this.$refs.cityStateZipError, this.$refs.cityInput.parentNode.querySelector('.asterisk'));
              removeError(this.$refs.modelState, this.$refs.cityStateZipError, document.querySelector('label[for="state"]').querySelector('.asterisk'));
              removeError(this.$refs.zipInput, this.$refs.cityStateZipError, this.$refs.zipInput.parentNode.querySelector('.asterisk'));
              removeError(this.$refs.zipPlusFour, this.$refs.cityStateZipError);
            }
          }
        },
        validateCISPhone() {
          if (this.phoneNumber && isAreaCodePhoneValid(this.phoneNumber) && this.phoneType) {
            removeError(this.$refs.phoneNumber, this.$refs.phoneNumberError);
            this.newPhoneNumberIsValid = true;
          } else {
            createError(this.$refs.phoneNumber, this.$refs.phoneNumberError, 'Please provide a valid phone number.');
            this.hasError = true;
            this.newPhoneNumberIsValid = false;
          }
        },

        populatePhoneNumbers(selectedAccount) {
          if (this.modelLdc !== 'NI' && this.modelLdc !== 'BS') {
            each(selectedAccount.phoneNumbers, num => {
              if (num.type === 'HOME') {
                this.modelHomePhone = num.number.replace(/[^0-9]/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
              } else if (num.type === 'BUSINESS') {
                this.modelBusinessPhone = num.number.replace(/[^0-9]/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
              } else {
                this.modelMobilePhone = num.number.replace(/[^0-9]/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
              }
            });
          } else {
            this.phoneNumbers = selectedAccount.phoneNumbers;
          }
        },

        populateMailingAddress(mailingAddress) {
          if (!mailingAddress) {
            return;
          }
          if (this.isCisAccount) {
            this.modelAddressType = mailingAddress.addressType;
          }
          switch (this.modelAddressType) {
            case 'Regular':
              if (this.isCisAccount) {
                this.populateRegularAddress(mailingAddress);
              } else {
                this.populateDisAddress(mailingAddress);
              }
              break;
            case 'Rural':
              this.populateRuralAddress(mailingAddress);
              break;
            case 'POBox':
              this.populatePoBoxAddress(mailingAddress);
              break;
            default:
              this.populateRegularAddress(mailingAddress);
              break;
          }
          this.modelCity = mailingAddress.city;
          this.modelState = mailingAddress.state;
          this.modelZip = mailingAddress.zip;
          this.modelZipPlusFour = mailingAddress.zipPlusFour;
          this.modelLot = mailingAddress.lot;
          this.overridden = mailingAddress.overridden;
        },

        populateDisAddress(mailingAddress) {
          this.modelDisAddress.addressLine1 = mailingAddress.addressLine1;
          this.modelDisAddress.addressLine2 = mailingAddress.addressLine2;
        },

        populateRuralAddress(mailingAddress) {
          this.modelRuralAddress.rrNumber = mailingAddress.ruralRoute;
          this.modelRuralAddress.rrBoxNumber = mailingAddress.ruralBox;
        },

        populatePoBoxAddress(mailingAddress) {
          this.modelPOBox = mailingAddress.poBoxNumber;
        },

        populateRegularAddress(mailingAddress) {
          this.modelStandardAddress.streetNumber = mailingAddress.streetNumber;
          this.modelStandardAddress.streetName = mailingAddress.streetName;
          this.modelStandardAddress.prefix = mailingAddress.streetPrefix;
          this.modelStandardAddress.suffix1 = mailingAddress.streetSuffix;
          this.modelStandardAddress.suffix2 = mailingAddress.streetDirectionalSuffix;
          this.modelStandardAddress.suite = mailingAddress.suite;
          this.modelStandardAddress.apartmentNumber = mailingAddress.apartment;
          this.modelStandardAddress.buildingNumber = mailingAddress.buildingNumber;
        },

        postFormContents() {
          this.hasError = false;

          if (!this.isCisAccount) {
            this.validateDISAddress();
            this.validateDISPhone();
          } else {
            this.validateCISAddress();
          }

          if (this.hasError) {
            // update live text to let the user know there are errors
            this.liveText = 'The form has errors. Please make sure your information is correct.';
            this.loading = false;
          } else {
            const requestData = this.buildRequestData();
            linkedAccountsApi.updateContactInfo(this.customerAccountId, requestData)
              .then(() => {
                this.loading = false;
                this.success = true;
                window.location.href = "/account/manage?updated=true";
              })
              .catch(this.handleApiError);
          }
        },

        buildRequestData() {
          const requestData = {
            phoneNumbers: this.buildPhoneRequest(),
            mailingAddress: this.buildMailingAddressRequest(),
          };
          return requestData;
        },

        buildPhoneRequest() {
          let requestPhones = [];
          if (this.modelLdc !== 'NI' && this.modelLdc !== 'BS') {
            if (this.modelHomePhone) {
              requestPhones.push({ type: 'HOME', number: this.modelHomePhone.replace(/\D/g, "") });
            }
            if (this.modelBusinessPhone) {
              requestPhones.push({ type: 'BUSINESS', number: this.modelBusinessPhone.replace(/\D/g, "") });
            }
            if (this.modelMobilePhone) {
              requestPhones.push({ type: 'MOBILE', number: this.modelMobilePhone.replace(/\D/g, "") });
            }
          } else {
            requestPhones = this.phoneNumbers.slice();
          }
          return requestPhones;
        },

        buildMailingAddressRequest() {
          let address = null;
          switch (this.modelAddressType) {
            case 'Regular':
              if (this.isCisAccount) {
                address = this.buildRegularAddressRequest();
              } else {
                address = this.buildDisAddressRequest();
              }
              break;
            case 'Rural':
              address = this.buildRuralAddressRequest();
              break;
            case 'POBox':
              address = this.buildPOBoxAddressRequest();
              break;
            default:
              address = this.buildRegularAddressRequest();
              break;
          }
          if (address) {
            address.addressType = this.modelAddressType;
            address.city = this.modelCity;
            address.state = this.modelState;
            address.zip = this.modelZip;
            address.zipPlusFour = this.modelZipPlusFour;
            address.lot = this.modelLot;
          }
          return address;
        },

        buildDisAddressRequest() {
          if (!this.modelDisAddress.addressLine1) {
            return null;
          }
          return {
            addressLine1: this.modelDisAddress.addressLine1,
            addressLine2: this.modelDisAddress.addressLine2,
          };
        },

        buildRuralAddressRequest() {
          if (!this.modelRuralAddress.rrNumber) {
            return null;
          }
          return {
            ruralRoute: this.modelRuralAddress.rrNumber,
            ruralBox: this.modelRuralAddress.rrBoxNumber,
          };
        },

        buildPOBoxAddressRequest() {
          if (!this.modelPOBox) {
            return null;
          }
          return {
            poBoxNumber: this.modelPOBox,
          };
        },

        buildRegularAddressRequest() {
          if (!this.modelStandardAddress.streetName) {
            return null;
          }
          return {
            streetNumber: this.modelStandardAddress.streetNumber,
            streetPrefix: this.modelStandardAddress.prefix,
            streetName: this.modelStandardAddress.streetName,
            streetSuffix: this.modelStandardAddress.suffix1,
            streetDirectionalSuffix: this.modelStandardAddress.suffix2,
            apartment: this.modelStandardAddress.apartmentNumber,
            suite: this.modelStandardAddress.suite,
            buildingNumber: this.modelStandardAddress.buildingNumber,
          };
        },

        clearMailingAddress() {
          // Shared Inputs
          this.modelCity = "";
          this.modelState = "";
          this.modelZip = "";
          this.modelZipPlusFour = "";
          this.modelLot = "";
          this.modelAdditionalText = "";

          // DIS Address
          this.modelDisAddress.addressLine1 = "";
          this.modelDisAddress.addressLine2 = "";

          // Standard Address
          this.modelStandardAddress.apartmentNumber = "";
          this.modelStandardAddress.buildingNumber = "";
          this.modelStandardAddress.prefix = "";
          this.modelStandardAddress.streetName = "";
          this.modelStandardAddress.streetNumber = "";
          this.modelStandardAddress.suffix1 = "";
          this.modelStandardAddress.suffix2 = "";
          this.modelStandardAddress.suite = "";

          // POBox Address
          this.modelPOBox = "";

          // Rural Address
          this.modelRuralAddress.rrNumber = "";
          this.modelRuralAddress.rrBoxNumber = "";

          // remove validation errors
          this.clearErrors();
        },

        clearErrors() {
          if (!this.isCisAccount) {
            removeError(this.$refs.addressLine1, this.$refs.addressLine1Error, this.$refs.addressLine1.parentNode.querySelector('.asterisk'));
            removeError(this.$refs.addressLine2, this.$refs.addressLine2Error);
          } else {
            const addressType = this.modelAddressType;

            if (addressType === 'Regular') {
              removeError(this.$refs.streetNumber, this.$refs.streetNumberError, this.$refs.streetNumber.parentNode.querySelector('.asterisk'));
              removeError(this.$refs.streetName, this.$refs.streetNameError, this.$refs.streetName.parentNode.querySelector('.asterisk'));
              removeError(this.$refs.lotInput, this.$refs.lotError);
            } else if (addressType === 'POBox') {
              removeError(this.$refs.poBox, this.$refs.poBoxError, this.$refs.poBox.parentNode.querySelector('.asterisk'));
            } else if (addressType === 'Rural') {
              removeError(this.$refs.rrNumber, this.$refs.rrNumberError, this.$refs.rrNumber.parentNode.querySelector('.asterisk'));
              removeError(this.$refs.rrBoxNumber, this.$refs.rrBoxNumberError, this.$refs.rrBoxNumber.parentNode.querySelector('.asterisk'));
            }
          }

          removeError(this.$refs.cityInput, this.$refs.cityStateZipError, this.$refs.cityInput.parentNode.querySelector('.asterisk'));
          removeError(this.$refs.modelState, this.$refs.cityStateZipError, document.querySelector('label[for="state"]').querySelector('.asterisk'));
          removeError(this.$refs.zipInput, this.$refs.cityStateZipError, this.$refs.zipInput.parentNode.querySelector('.asterisk'));
          removeError(this.$refs.zipPlusFour, this.$refs.cityStateZipError);
        },
      },
    });
  }
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/portal/editContactInfo.js